<div class="wizard-container">
  <!-- Toast Notifications -->
  <p-toast position="top-right" role="status" aria-live="polite" aria-atomic="true" />

  <!-- Live Region for Announcements -->
  <div role="status" aria-live="polite" aria-atomic="true" class="visually-hidden">
    {{ liveRegionMessage() }}
  </div>

  <app-validation-error-summary
    [sections]="validationErrorSections()"
    (closed)="validationErrorSections.set([])"
  />

  <!-- Modification Request Banner -->
  @if (hasModificationRequest()) {
    <div class="modification-request-banner mb-4 p-4 border-round-lg surface-0 border-2 border-orange-500 shadow-3" role="alert" aria-live="assertive">
      <div class="flex align-items-start gap-3">
        <div class="flex-shrink-0">
          <i class="pi pi-exclamation-triangle text-orange-500 text-3xl" aria-hidden="true"></i>
        </div>
        <div class="flex-1">
          <h3 class="text-orange-900 font-bold text-xl m-0 mb-2">
            <i class="pi pi-refresh mr-2" aria-hidden="true"></i>
            Richiesta di Modifica
          </h3>
          <p class="text-orange-800 text-base m-0 mb-3">
            AXA ha richiesto delle modifiche alla tua registrazione. Si prega di leggere attentamente le note seguenti e apportare le correzioni necessarie.
          </p>
          @if (modificationComment()) {
            <div class="surface-50 border-round p-3 border-left-3 border-orange-500">
              <p class="text-sm font-semibold text-600 m-0 mb-2">
                <i class="pi pi-comment mr-2" aria-hidden="true"></i>
                Note del Revisore:
              </p>
              <p class="text-base text-900 m-0 white-space-pre-wrap">{{ displayedComment() }}</p>
              @if (isCommentLong()) {
                <button 
                  type="button"
                  class="text-sm text-orange-600 hover:text-orange-700 font-semibold cursor-pointer border-none bg-transparent p-0 mt-2"
                  (click)="toggleModificationNotes()"
                  [attr.aria-expanded]="isModificationNotesExpanded()"
                  aria-controls="modification-notes">
                  <i class="pi mr-1" [class.pi-chevron-down]="!isModificationNotesExpanded()" [class.pi-chevron-up]="isModificationNotesExpanded()" aria-hidden="true"></i>
                  {{ isModificationNotesExpanded() ? 'Mostra meno' : 'Visualizza altro' }}
                </button>
              }
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- Page Header -->
  @if (!professionalIdInput()) {
    <section class="page-header mb-5" role="region" aria-labelledby="collaborators-form-title">
      <div class="header-content">
        <div class="header-left">
          <i class="pi pi-users header-icon" aria-hidden="true"></i>
          <div class="header-text">
            <h1 id="collaborators-form-title">Collaboratori Professionisti e Impiegati Amministrativi</h1>
            <p class="professional-info">
              @if (professionalSummaryBusinessName()) {
                <strong>{{ professionalSummaryBusinessName() }}</strong><br>
              }
              @if (professionalIdDisplay()) {
                ID: {{ professionalIdDisplay() }}
              }
              @if (professionalSummaryType()) {
                &nbsp;·&nbsp;{{ professionalSummaryType() }}
              }
            </p>
            <p class="subtitle">Inserisci i collaboratori professionisti e gli impiegati amministrativi dell'anagrafica</p>
          </div>
        </div>
      </div>
    </section>
  }

  <!-- Read-Only Mode Banner (page-level) -->
  @if (!professionalIdInput() && isReadOnly()) {
    <div class="read-only-banner" role="alert">
      <div class="flex align-items-center gap-3 mb-2">
        <i class="pi pi-eye" aria-hidden="true"></i>
        <strong>Modalità Sola Lettura</strong>
      </div>
      <span>Stai visualizzando l'anagrafica inviata. I documenti sono disponibili solo per il download.</span>
    </div>
  }

  <!-- Step Content -->
  <div class="step-content" role="form" aria-labelledby="collaborators-form-title">
    <!-- Collaboratori Professionisti subsection (per-type behaviour) -->
    <app-collaborators-subsection
      #collabSub
      [professionalType]="collaboratorProfessionalType()"
      [professionalId]="professionalIdDisplay()"
      [readonly]="isReadOnly()"
    ></app-collaborators-subsection>

    <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="impiegati-subsection-title">
      <div class="flex align-items-center gap-2 mb-3">
        <i class="pi pi-id-card text-xl" style="color: #00125b;" aria-hidden="true"></i>
        <h2 id="impiegati-subsection-title" class="text-xl font-semibold m-0">Impiegati Amministrativi</h2>
      </div>

      @if (!professionalIdInput() && !isReadOnly()) {
        <div class="surface-100 border-round p-3 mb-3" role="region" aria-labelledby="impiegati-notice-title">
          <p id="impiegati-notice-title" class="text-sm text-700 m-0">
            <i class="pi pi-exclamation-triangle mr-2" style="color: #f59e0b;" aria-hidden="true"></i>
            <strong>Nota importante:</strong> Funzione disponibile solo per gli “Impiegati Amministrativi” che necessitano di accedere ai sistemi gestionali sinistri di AXA.
          </p>
        </div>
        <div class="surface-100 border-round p-3 mb-4" role="region" aria-labelledby="impiegati-instructions-title">
          <p id="impiegati-instructions-title" class="text-sm text-700 m-0">
            <i class="pi pi-info-circle mr-2" aria-hidden="true"></i>
            <strong>Istruzioni:</strong> Aggiungi uno o più impiegati amministrativi compilando tutti i campi obbligatori (contrassegnati con *). Puoi comprimere ciascuna scheda per una visione d'insieme.
          </p>
        </div>
      }

      @for (collaborator of collaborators(); track collaborator.id; let idx = $index) {
        <div class="surface-0 border-round p-4 mb-3 shadow-1">
          <!-- Header Card con numerazione + toggle collasso -->
          <div class="surface-100 border-round p-3 border-1 border-300" [class.mb-4]="collaborator.expanded">
            <div class="flex justify-content-between align-items-center gap-2">
              <button
                type="button"
                class="flex align-items-center gap-2 flex-1 border-none bg-transparent p-0 cursor-pointer text-left"
                (click)="toggleImpiegatoExpanded(collaborator.id)"
                [attr.aria-expanded]="collaborator.expanded"
                [attr.aria-label]="(collaborator.expanded ? 'Comprimi' : 'Espandi') + ' impiegato amministrativo ' + (idx + 1)"
              >
                <i class="pi" [ngClass]="collaborator.expanded ? 'pi-chevron-down' : 'pi-chevron-right'" aria-hidden="true"></i>
                <i class="pi pi-user text-xl" style="color: #00125b;" aria-hidden="true"></i>
                <h4 class="text-lg font-semibold m-0">
                  Impiegato amministrativo #{{ idx + 1 }}
                  @if (!collaborator.expanded && collapsedImpiegatoTitle(collaborator)) {
                    <span class="font-normal text-600"> — {{ collapsedImpiegatoTitle(collaborator) }}</span>
                  } @else if (collaborator.expanded && collaborator.cognome && collaborator.nome) {
                    <span class="font-normal text-600"> - {{ collaborator.cognome }} {{ collaborator.nome }}</span>
                  }
                </h4>
              </button>
              @if (!isReadOnly()) {
                <button
                  pButton
                  type="button"
                  icon="pi pi-trash"
                  class="p-button-text p-button-danger p-button-sm"
                  [attr.aria-label]="'Rimuovi collaboratore ' + (idx + 1)"
                  (click)="removeCollaborator(collaborator.id)"
                ></button>
              }
            </div>
          </div>

          @if (collaborator.expanded) {
          <!-- Collaborator Form Fields con grid -->
          <div class="grid">
            <!-- Cognome -->
            <div class="field col-12 md:col-4">
              <label [for]="'cognome-' + collaborator.id" class="font-semibold block mb-2">
                Cognome <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                pInputText
                type="text"
                [id]="'cognome-' + collaborator.id"
                #firstInput
                [(ngModel)]="collaborator.cognome"
                (blur)="markImpiegatoFieldTouched(collaborator.id, 'cognome')"
                placeholder="Inserisci cognome"
                class="w-full"
                [disabled]="isReadOnly()"
                [attr.aria-required]="true"
                [attr.aria-invalid]="collaborator.cognomeTouched && isMissingMeaningfulText(collaborator.cognome)"
              />
              @if (collaborator.cognomeTouched && isMissingMeaningfulText(collaborator.cognome)) {
                <small class="text-red-500 block mt-1" role="alert">Campo obbligatorio</small>
              }
            </div>

            <!-- Nome -->
            <div class="field col-12 md:col-4">
              <label [for]="'nome-' + collaborator.id" class="font-semibold block mb-2">
                Nome <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                pInputText
                type="text"
                [id]="'nome-' + collaborator.id"
                [(ngModel)]="collaborator.nome"
                (blur)="markImpiegatoFieldTouched(collaborator.id, 'nome')"
                placeholder="Inserisci nome"
                class="w-full"
                [disabled]="isReadOnly()"
                [attr.aria-required]="true"
                [attr.aria-invalid]="collaborator.nomeTouched && isMissingMeaningfulText(collaborator.nome)"
              />
              @if (collaborator.nomeTouched && isMissingMeaningfulText(collaborator.nome)) {
                <small class="text-red-500 block mt-1" role="alert">Campo obbligatorio</small>
              }
            </div>

            <!-- Codice Fiscale -->
            <div class="field col-12 md:col-4">
              <label [for]="'cf-' + collaborator.id" class="font-semibold block mb-2">
                Codice Fiscale
                <span class="text-red-500" aria-hidden="true">*</span>
                <i
                  class="pi pi-info-circle ml-2 text-primary cursor-help"
                  pTooltip="16 caratteri per persone fisiche, 11 cifre per aziende"
                  tooltipPosition="bottom"
                  tabindex="0"
                  role="img"
                  aria-label="Il codice fiscale deve essere di 16 caratteri per persone fisiche o 11 cifre per aziende"
                ></i>
              </label>
              <input
                pInputText
                type="text"
                [id]="'cf-' + collaborator.id"
                [ngModel]="collaborator.codiceFiscale"
                (ngModelChange)="onCodiceFiscaleChange(collaborator.id, $event)"
                (blur)="markImpiegatoFieldTouched(collaborator.id, 'codiceFiscale')"
                placeholder="es. RSSMRA80A01H501U"
                class="w-full"
                [disabled]="isReadOnly()"
                [class.p-invalid]="collaborator.codiceFiscaleTouched && collaborator.codiceFiscaleError"
                [attr.aria-required]="true"
                [attr.aria-invalid]="collaborator.codiceFiscaleTouched && !!collaborator.codiceFiscaleError"
                [attr.aria-describedby]="collaborator.codiceFiscaleTouched && collaborator.codiceFiscaleError ? 'cf-error-' + collaborator.id : null"
              />
              @if (collaborator.codiceFiscaleTouched && collaborator.codiceFiscaleError) {
                <small [id]="'cf-error-' + collaborator.id" class="text-red-500 block mt-1" role="alert">
                  {{ collaborator.codiceFiscaleError }}
                </small>
              }
            </div>

          </div>
          }
        </div>
      } @empty {
        <!-- Messaggio quando non ci sono collaboratori -->
        <div class="surface-100 border-round p-5 text-center">
          <i class="pi pi-info-circle text-5xl text-primary mb-3" style="color: #00125b;" aria-hidden="true"></i>
          <h3 class="text-xl font-semibold mb-2 text-800">Nessun Impiegato amministrativo aggiunto</h3>
          @if (!isReadOnly()) {
            <p class="text-600 mb-0">
              Clicca sul pulsante "Aggiungi Impiegato amministrativo" per iniziare ad inserire i dati dei tuoi impiegati.
            </p>
          }
        </div>
      }

      <!-- Add Collaborator Button -->
      @if (!isReadOnly()) {
        <div class="flex justify-content-end mt-4">
          <button
            pButton
            type="button"
            label="Aggiungi Impiegato amministrativo"
            icon="pi pi-plus"
            class="p-button-outlined"
            [attr.aria-label]="'Aggiungi nuovo impiegato amministrativo'"
            (click)="addCollaborator()"
          ></button>
        </div>
      }
    </section>

    <!-- Collaboratori subsection (per-type behaviour) -->
  </div>

  <!-- Footer with Action Buttons -->
  @if (!professionalIdInput() && !isReadOnly()) {
    <div class="flex flex-column align-items-end gap-2 mt-5">
      <div class="flex justify-content-end gap-2">
        <button
          pButton
          type="button"
          label="Salva bozza"
          icon="pi pi-save"
          class="p-button-outlined"
          [attr.aria-label]="'Salva bozza dei collaboratori professionisti e degli impiegati amministrativi'"
          (click)="onSalvaBozza()"
        ></button>

        <button
          pButton
          type="button"
          label="Salva e prosegui"
          icon="pi pi-arrow-right"
          iconPos="right"
          [attr.aria-label]="'Salva i dati dei collaboratori professionisti e degli impiegati amministrativi e prosegui'"
          class="p-button-primary"
          (click)="onSalvaEProsegui()"
        ></button>
      </div>
    </div>
  }
</div>
